import { Card, Row, Col, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { 
  faBuilding, 
  faFolder, 
  faHashtag, 
  faInfoCircle,
  faIdCard
} from "@fortawesome/free-solid-svg-icons";
import { useGetProjectById } from "../../../services/projectService";
import { CSSProperties, useState } from "react";

type ProjectSummaryProps = {
  projectId: string;
};

// Styles for the clickable cards
const cardStyle: CSSProperties = {
  transition: "all 0.2s ease-in-out",
  cursor: "pointer",
  position: "relative"
};

const cardHoverStyle: CSSProperties = {
  backgroundColor: "#f0f0f0",
  transform: "translateY(-2px)",
  boxShadow: "0 .125rem .25rem rgba(0,0,0,.075)"
};

export const ProjectSummary = ({ projectId }: ProjectSummaryProps) => {
  const { data: project, isLoading, isError } = useGetProjectById(projectId);
  const [hoveredOffice, setHoveredOffice] = useState(false);
  const [hoveredFolder, setHoveredFolder] = useState(false);

  if (isLoading) {
    return (
      <Card className="py-4 rounded-3 shadow-sm border-0">
        <Card.Body className="p-4">
          <div className="d-flex align-items-center gap-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span>Loading project information...</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (isError || !project) {
    return (
      <Card className="mb-4 rounded-3 shadow-sm border-0 bg-light">
        <Card.Body className="p-4">
          <div className="d-flex align-items-center gap-2 text-muted">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Could not load project information</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4 rounded-3 shadow-sm border-0">
      <Card.Body className="p-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
          <div>
            <h4 className="mb-1 fw-bold">{project.name}</h4>
            {project.description && (
              <p className="text-muted mb-0">{project.description}</p>
            )}
          </div>
          <Badge 
            bg={project.projectStatus === "Active" ? "success" : "secondary"}
            className="py-2 px-3 rounded-pill d-flex align-items-center fs-6 mt-2 mt-md-0"
          >
            {project.projectStatus}
          </Badge>
        </div>
        
        <Row className="g-3 mt-1">
          <Col md={6} lg={3}>
            <div className="d-flex align-items-center gap-2 p-2 bg-light rounded-3">
              <FontAwesomeIcon icon={faHashtag} className="text-primary" />
              <div>
                <div className="small text-muted">Project Number</div>
                <div className="fw-medium">{project.projectNumber}</div>
              </div>
            </div>
          </Col>
          
          <Col md={6} lg={3}>
            <Link 
              to={`/offices/${project.officeId}`} 
              className="text-decoration-none text-body"
              onMouseEnter={() => setHoveredOffice(true)}
              onMouseLeave={() => setHoveredOffice(false)}
            >
              <div 
                className="d-flex align-items-center gap-2 p-2 bg-light rounded-3"
                style={{
                  ...cardStyle,
                  ...(hoveredOffice ? cardHoverStyle : {})
                }}
              >
                <FontAwesomeIcon icon={faBuilding} className="text-primary" />
                <div>
                  <div className="small text-muted">Office</div>
                  <div className="fw-medium">{project.officeName}</div>
                </div>
                <div className="ms-auto">
                  <span className="small text-primary">View →</span>
                </div>
              </div>
            </Link>
          </Col>
          
          <Col md={6} lg={3}>
            <Link 
              to={`/folders/${project.folderId}`} 
              className="text-decoration-none text-body"
              onMouseEnter={() => setHoveredFolder(true)}
              onMouseLeave={() => setHoveredFolder(false)}
            >
              <div 
                className="d-flex align-items-center gap-2 p-2 bg-light rounded-3"
                style={{
                  ...cardStyle,
                  ...(hoveredFolder ? cardHoverStyle : {})
                }}
              >
                <FontAwesomeIcon icon={faFolder} className="text-primary" />
                <div>
                  <div className="small text-muted">Folder</div>
                  <div className="fw-medium">{project.folderName}</div>
                </div>
                <div className="ms-auto">
                  <span className="small text-primary">View →</span>
                </div>
              </div>
            </Link>
          </Col>
          
          {project.foreignId && (
            <Col md={6} lg={3}>
              <div className="d-flex align-items-center gap-2 p-2 bg-light rounded-3">
                <FontAwesomeIcon icon={faIdCard} className="text-primary" />
                <div>
                  <div className="small text-muted">Foreign ID</div>
                  <div className="fw-medium">{project.foreignId}</div>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
}; 